import { Box, Typography } from '@mui/material';
import { PaperFrameStyled } from '@/components/common/paperFrameStyled';
import { formatNumberToCurrency } from '@/utils/currency';
import { NetCashFlow } from '@/interfaces/cashFlow';
import HorizontalTwoValuesChart from '@/components/charts/horizontalTwoValuesChart';
import ChartCurrencyTitle from '@/components/charts/chartCurrencyTitle';
import { useLocalization } from '@/hooks/useLocalization';
import { useGraphColors } from '@/hooks/useGraphColors';

const CashFlowNet: React.FC<{ data: NetCashFlow }> = ({ data }) => {
  const { t } = useLocalization();
  const graphColors = useGraphColors();
  const incomeColor = graphColors[0] ?? '#6FDD98';
  const spendingColor = graphColors[1] ?? '#F29559';
  const title = (
    <Typography
      variant='h5'
      sx={{
        fontSize: '24px',
        lineHeight: '133.4%',
      }}
    >
      {t('netCashFlow')}
    </Typography>
  );

  const content = (
    <Box
      sx={{
        display: 'flex',
        padding: '0 24px 24px 24px',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: '24px',
        alignSelf: 'stretch',
        flexGrow: 1,
      }}
    >
      <ChartCurrencyTitle
        totalValue={data.realIncome}
        otherValue={data.change.amount}
        percentage={data.change.percentage}
        compareText='Past year'
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
        }}
      >
        <HorizontalTwoValuesChart
          totalValue={data.income + data.spending}
          secondValue={data.spending}
          firstColor={incomeColor}
          secondColor={spendingColor}
        />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '4px',
            alignSelf: 'stretch',
            mt: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              alignSelf: 'stretch',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                flex: '1 0 0',
              }}
            >
              <Box
                sx={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '100px',
                  backgroundColor: incomeColor,
                }}
              ></Box>
              <Typography
                variant='body2'
                sx={{
                  fontSize: '14px',
                  fontWeight: 400,
                  lineHeight: '143%',
                  letterSpacing: '0.17px',
                }}
              >
                {t('income')}
              </Typography>
            </Box>
            <Typography
              variant='body2'
              sx={{
                textAlign: 'right',
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: '143%',
                letterSpacing: '0.17px',
              }}
            >
              {formatNumberToCurrency(data.income)}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              alignSelf: 'stretch',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                flex: '1 0 0',
              }}
            >
              <Box
                sx={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '100px',
                  backgroundColor: spendingColor,
                }}
              ></Box>
              <Typography
                variant='body2'
                sx={{
                  fontSize: '14px',
                  fontWeight: 400,
                  lineHeight: '143%',
                  letterSpacing: '0.17px',
                }}
              >
                {t('spending')}
              </Typography>
            </Box>
            <Typography
              variant='body2'
              sx={{
                textAlign: 'right',
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: '143%',
                letterSpacing: '0.17px',
              }}
            >
              {formatNumberToCurrency(data.spending)}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  return (
    <PaperFrameStyled
      sx={{
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          padding: '24px',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: '24px',
          alignSelf: 'stretch',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            alignSelf: 'stretch',
          }}
        >
          {title}
        </Box>
      </Box>
      {content}
    </PaperFrameStyled>
  );
};

export default CashFlowNet;
